// ============================================================
// SLIK BEAUTY — MULTI-STAFF BOOKING SYSTEM (FIXED)
// Each service gets its own specialist - no "one therapist does all" constraint
// ============================================================

import { useState, useEffect } from "react";

// ─── CONFIG ────────────────────────────────────────────────────
const SUPABASE_CONFIG = {
  url:     process.env.REACT_APP_SUPABASE_URL,
  anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY,
};

const EMAILJS_CONFIG = {
  serviceId:  "service_g5ygchi",
  templateId: "template_up0cf9o",
  publicKey:  process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

const ADMIN_PASSWORD = "SlikAdmin2026";
const SLIK_LOGO_URL  = "";

// ─── SUPABASE CLIENT ─────────────────────────────────────────
const sb = {
  ready: () => SUPABASE_CONFIG.url && SUPABASE_CONFIG.url !== "undefined",
  async insert(row) {
    if (!this.ready()) return null;
    try {
      const r = await fetch(`${SUPABASE_CONFIG.url}/rest/v1/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_CONFIG.anonKey, "Authorization": `Bearer ${SUPABASE_CONFIG.anonKey}`, "Prefer": "return=representation" },
        body: JSON.stringify(row),
      });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  },
  async fetchAll() {
    if (!this.ready()) return null;
    try {
      const r = await fetch(`${SUPABASE_CONFIG.url}/rest/v1/bookings?order=created_at.desc`, { headers: { "apikey": SUPABASE_CONFIG.anonKey, "Authorization": `Bearer ${SUPABASE_CONFIG.anonKey}` } });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  },
  async patch(id, data) {
    if (!this.ready()) return false;
    try {
      const r = await fetch(`${SUPABASE_CONFIG.url}/rest/v1/bookings?id=eq.${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", "apikey": SUPABASE_CONFIG.anonKey, "Authorization": `Bearer ${SUPABASE_CONFIG.anonKey}` }, body: JSON.stringify(data) });
      return r.ok;
    } catch { return false; }
  },
};

// ─── EMAILJS ──────────────────────────────────────────────────
async function sendEmail(booking) {
  const { serviceId, templateId, publicKey } = EMAILJS_CONFIG;
  if (!serviceId || !templateId || !publicKey || serviceId.startsWith("YOUR_")) return;
  try {
    await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ service_id: serviceId, template_id: templateId, user_id: publicKey,
        template_params: { to_name: booking.client_name, to_email: booking.client_email, booking_id: booking.id,
          service: booking.service_name, staff: booking.staff_name, date: fmtLong(booking.date), time: booking.time,
          duration: `${booking.service_duration} minutes`, price: `£${booking.service_price}`,
          location: "47 Broad Street Mall, Reading RG1 7QE", phone: "+44 1183271870" } }),
    });
  } catch (e) { console.error("EmailJS error", e); }
}

// ─── LOCALSTORAGE ─────────────────────────────────────────────
const LS = "slik_bks_v2";
const lsGet = () => { try { return JSON.parse(localStorage.getItem(LS) || "[]"); } catch { return []; } };
const lsSet = d => { try { localStorage.setItem(LS, JSON.stringify(d)); } catch {} };

// ─── BRAND COLORS ─────────────────────────────────────────────
const B = {
  plum: "#5C2D5C", plumDark: "#3D1A3D", plumMid: "#7B4A7B", plumBg: "#F5EEF5",
  blush: "#FAF5F8", champagne: "#F5EDE0", gold: "#C4963A", goldLight: "#E8C97A",
  white: "#FFFFFF", ink: "#1C0D1C", muted: "#8A6A8A", border: "#E4D4E4",
  green: "#3D7A56", red: "#9B3535", blue: "#3D5A9B",
};

// ─── SERVICES ─────────────────────────────────────────────────
const SERVICES = [
  // CLINIC
  { id: "aw1", sec: "clinic", cat: "Anti-Wrinkle", name: "Anti-Wrinkle — 1 Area", price: 90, dur: 30 },
  { id: "aw2", sec: "clinic", cat: "Anti-Wrinkle", name: "Anti-Wrinkle — 2 Areas", price: 150, dur: 30 },
  { id: "aw3", sec: "clinic", cat: "Anti-Wrinkle", name: "Anti-Wrinkle — 3 Areas", price: 200, dur: 30 },
  { id: "df1", sec: "clinic", cat: "Dermal Filler", name: "Lips Filler Classic 1ml", price: 120, dur: 45 },
  { id: "df2", sec: "clinic", cat: "Dermal Filler", name: "Cheeks Enhancement", price: 190, dur: 60 },
  { id: "mn1", sec: "clinic", cat: "Microneedling", name: "Anti-Ageing Face", price: 95, dur: 60 },
  { id: "mn2", sec: "clinic", cat: "Microneedling", name: "Anti-Ageing Neck", price: 85, dur: 50 },
  { id: "el1", sec: "clinic", cat: "Eyebrows & Lashes", name: "Korean Lash Lift", price: 55, dur: 60 },
  { id: "el2", sec: "clinic", cat: "Eyebrows & Lashes", name: "Brow Lamination", price: 50, dur: 50 },
  // SALON
  { id: "fc1", sec: "salon", cat: "Facials", name: "Deep Cleansing Facial", price: 40, dur: 40 },
  { id: "fc2", sec: "salon", cat: "Facials", name: "Skin Brightening Facial", price: 45, dur: 45 },
  { id: "na1", sec: "salon", cat: "Manicure", name: "Mini Manicure", price: 15, dur: 30 },
  { id: "na2", sec: "salon", cat: "Manicure", name: "Spa Manicure", price: 25, dur: 45 },
  { id: "na11", sec: "salon", cat: "Manicure", name: "Kids Mini Manicure", price: 10, dur: 25 },
  { id: "hr2", sec: "salon", cat: "Hairdressing", name: "Ladies Hair Cut", price: 25, dur: 45 },
  { id: "hr5", sec: "salon", cat: "Hairdressing", name: "Full Head Colour", price: 60, dur: 90 },
  { id: "wb4", sec: "salon", cat: "Body Waxing", name: "Brazilian Wax", price: 30, dur: 30 },
];

// ─── STAFF (with their specialties) ───────────────────────────
const STAFF = [
  { id: "sarah", name: "Sarah", init: "SA", role: "Senior Hair Stylist", rating: 5.0, reviews: 7, section: "salon", cats: ["Hairdressing"] },
  { id: "preeti", name: "Preeti", init: "PR", role: "Beauty Therapist", rating: 4.8, reviews: 380, section: "salon", cats: ["Facials", "Manicure", "Body Waxing"] },
  { id: "sita", name: "Sita", init: "SI", role: "Beauty Therapist", rating: 4.8, reviews: 274, section: "salon", cats: ["Facials", "Manicure"] },
  { id: "domina", name: "Domina", init: "DO", role: "Lash Specialist", rating: 4.7, reviews: 199, section: "clinic", cats: ["Eyebrows & Lashes"] },
  { id: "laura", name: "Laura", init: "LA", role: "Nail & Lash Tech", rating: 4.9, reviews: 110, section: "salon", cats: ["Manicure", "Eyebrows & Lashes"] },
  { id: "gloria", name: "Gloria", init: "GL", role: "Senior Aesthetician", rating: 4.8, reviews: 249, section: "clinic", cats: ["Anti-Wrinkle", "Dermal Filler", "Microneedling"] },
  { id: "nimra", name: "Nimra", init: "NI", role: "Aesthetician", rating: 4.4, reviews: 59, section: "clinic", cats: ["Anti-Wrinkle", "Microneedling"] },
  { id: "kida", name: "Kida", init: "KI", role: "Nail Technician", rating: 4.9, reviews: 38, section: "salon", cats: ["Manicure"] },
];

// ─── TIME SLOTS ───────────────────────────────────────────────
const SLOTS_WEEKDAY = ["09:00","09:30","10:00","10:30","11:00","11:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30"];
const SLOTS_SUNDAY = ["11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30"];

// ─── HELPERS ──────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DSHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const calDays = (y, m) => { const f = new Date(y, m, 1).getDay(), t = new Date(y, m + 1, 0).getDate(), a = Array(f).fill(null); for (let i = 1; i <= t; i++) a.push(i); return a; };
const toDS = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const fmtLong = s => s ? new Date(s + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
const fmtShort = s => s ? new Date(s + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
const isSunday = s => s ? new Date(s + "T12:00:00").getDay() === 0 : false;
const genId = () => "SB" + Date.now().toString(36).toUpperCase();
const totalPrice = services => services.reduce((s, x) => s + x.price, 0);
const totalDur = services => services.reduce((s, x) => s + x.dur, 0);

const initBk = () => ({
  services: [],           // array of selected service objects
  staffAssignments: {},  // { serviceId: staffId }
  date: null,
  time: null,
  client: { name: "", email: "", phone: "", notes: "" },
});

// ─── CSS ──────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Nunito:wght@300;400;500;600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{background:${B.blush};font-family:'Nunito',sans-serif}
.pf{font-family:'Playfair Display',serif}
.nu{font-family:'Nunito',sans-serif}
.fade{animation:fadeIn 0.3s ease}
@keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
.crd{border:1.5px solid ${B.border};cursor:pointer;transition:all 0.2s;background:${B.white};border-radius:12px}
.crd:hover{border-color:${B.plumMid};box-shadow:0 4px 12px rgba(92,45,92,0.1);transform:translateY(-2px)}
.crd-sel{border-color:${B.plum}!important;background:${B.plumBg}!important}
.btn-plum{background:${B.plum};color:white;border:none;padding:12px 28px;border-radius:50px;font-weight:700;font-size:13px;cursor:pointer;transition:all 0.2s;font-family:'Nunito',sans-serif}
.btn-plum:hover{background:${B.plumDark};transform:translateY(-1px);box-shadow:0 4px 12px rgba(92,45,92,0.3)}
.btn-plum:disabled{opacity:0.4;cursor:not-allowed;transform:none}
.btn-gold{background:${B.gold};color:white;border:none;padding:12px 28px;border-radius:50px;font-weight:700;font-size:13px;cursor:pointer;transition:all 0.2s;font-family:'Nunito',sans-serif}
.btn-gold:hover{background:${B.goldLight};transform:translateY(-1px)}
.btn-out{background:transparent;border:1.5px solid ${B.border};color:${B.muted};padding:10px 24px;border-radius:50px;font-weight:600;font-size:13px;cursor:pointer;transition:all 0.2s;font-family:'Nunito',sans-serif}
.btn-out:hover{border-color:${B.plum};color:${B.plum};background:${B.plumBg}}
.cat-header{cursor:pointer;padding:12px 16px;background:${B.white};border-radius:10px;margin-bottom:6px;display:flex;justify-content:space-between;align-items:center;border:1px solid ${B.border}}
.cat-header:hover{background:${B.plumBg}}
.service-item{padding:10px 16px 10px 32px;margin-bottom:2px;cursor:pointer;display:flex;justify-content:space-between;align-items:center;border-radius:8px}
.service-item:hover{background:${B.plumBg}}
.service-sel{background:${B.plumBg}!important;border-left:3px solid ${B.plum}}
.section-card{background:${B.white};border-radius:20px;padding:20px;border:2px solid ${B.border}}
.section-clinic{border-top:4px solid ${B.plum}}
.section-salon{border-top:4px solid ${B.gold}}
.selected-pill{background:${B.plumBg};border-radius:50px;padding:6px 14px;display:inline-flex;align-items:center;gap:10px;margin:4px}
.staff-option{padding:12px 16px;border:1.5px solid ${B.border};border-radius:12px;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:12px}
.staff-option:hover{border-color:${B.plum};background:${B.plumBg}}
.staff-selected{border-color:${B.plum}!important;background:${B.plumBg}!important}
.tslot{border:1.5px solid ${B.border};cursor:pointer;padding:10px 8px;text-align:center;border-radius:10px;font-size:13px;font-weight:600;transition:all 0.15s}
.tslot:hover{border-color:${B.plum};color:${B.plum};background:${B.plumBg}}
.tslot-sel{background:${B.plum}!important;color:white!important;border-color:${B.plum}!important}
.tslot-dis{opacity:0.3;cursor:not-allowed;pointer-events:none}
.cal-day{cursor:pointer;padding:8px 4px;text-align:center;border-radius:50%;font-size:13px;font-weight:600;transition:all 0.15s}
.cal-day:hover{background:${B.plumBg};color:${B.plum}}
.cal-day-sel{background:${B.plum}!important;color:white!important}
.cal-day-dis{color:#ccc!important;cursor:not-allowed;pointer-events:none}
.badge-c{background:${B.plumBg};color:${B.plum};padding:4px 12px;border-radius:20px;font-size:10px;font-weight:700}
.badge-s{background:${B.champagne};color:${B.gold};padding:4px 12px;border-radius:20px;font-size:10px;font-weight:700}
`;

// ─── COMPONENTS ───────────────────────────────────────────────
const SLbl = ({ c }) => <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: B.muted, marginBottom: "5px" }}>{c}</div>;
const GoldLine = () => <div style={{ width: 60, height: "2px", background: `linear-gradient(90deg,transparent,${B.gold},transparent)`, margin: "12px auto" }} />;

const SlikMark = ({ light = false, small = false }) => {
  if (SLIK_LOGO_URL) return <img src={SLIK_LOGO_URL} alt="Slik Beauty" style={{ height: small ? 32 : 40 }} />;
  return (
    <div style={{ textAlign: "center", lineHeight: 1 }}>
      <span className="pf" style={{ fontSize: small ? 22 : 28, fontWeight: 700, color: light ? "#fff" : B.plum }}>
        <span style={{ fontStyle: "italic", color: light ? B.goldLight : B.gold }}>S</span>LIK
      </span>
      <div className="nu" style={{ fontSize: small ? 8 : 9, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: light ? "rgba(255,255,255,0.5)" : B.muted, marginTop: 2 }}>BEAUTY</div>
    </div>
  );
};

const NavRow = ({ onBack, onNext, canNext, label = "Continue →" }) => (
  <div style={{ display: "flex", justifyContent: onBack ? "space-between" : "flex-end", marginTop: 32 }}>
    {onBack && <button className="btn-out" onClick={onBack}>← Back</button>}
    <button className="btn-plum" onClick={onNext} disabled={!canNext}>{label}</button>
  </div>
);

// ─── MAIN APP ─────────────────────────────────────────────────
export default function SlikBooking() {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [lastId, setLastId] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bks, setBks] = useState([]);
  const [bk, setBk] = useState(initBk);
  const [cal, setCal] = useState({ y: new Date().getFullYear(), m: new Date().getMonth() });
  const [view, setView] = useState("book");
  const [pwd, setPwd] = useState("");
  const [pwdErr, setPwdErr] = useState(false);
  const [openCats, setOpenCats] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (sb.ready()) {
        const remote = await sb.fetchAll();
        if (remote && remote.length) { setBks(remote); lsSet(remote); }
      }
      const local = lsGet();
      if (local.length) setBks(local);
      setLoading(false);
    })();
  }, []);

  const persist = (rows) => { setBks(rows); lsSet(rows); };
  const updateStatus = async (id, status) => {
    const updated = bks.map(b => b.id === id ? { ...b, status } : b);
    persist(updated);
    await sb.patch(id, { status });
  };

  const confirmBooking = async () => {
    setSending(true);
    const selectedServices = bk.services;
    const total = totalPrice(selectedServices);
    const dur = totalDur(selectedServices);
    
    // Build staff assignment string for email
    const staffAssignmentsList = Object.entries(bk.staffAssignments).map(([serviceId, staffId]) => {
      const service = selectedServices.find(s => s.id === serviceId);
      const staff = STAFF.find(s => s.id === staffId);
      return `${service?.name} with ${staff?.name}`;
    }).join("; ");
    
    const entry = {
      id: genId(), created_at: new Date().toISOString(),
      section: selectedServices.some(s => s.sec === "clinic") ? (selectedServices.some(s => s.sec === "salon") ? "both" : "clinic") : "salon",
      category: [...new Set(selectedServices.map(s => s.cat))].join(", "),
      service_name: selectedServices.map(s => s.name).join(" + "),
      service_price: total,
      service_duration: dur,
      services_json: JSON.stringify(selectedServices),
      staff_name: staffAssignmentsList,
      date: bk.date, time: bk.time,
      client_name: bk.client.name, client_email: bk.client.email, client_phone: bk.client.phone, client_notes: bk.client.notes,
      status: "confirmed",
    };
    await sb.insert(entry);
    persist([...bks, entry]);
    await sendEmail(entry);
    setSending(false);
    setLastId(entry.id);
    setDone(true);
  };

  const toggleService = (service) => {
    setBk(b => {
      const isSelected = b.services.some(s => s.id === service.id);
      let newServices, newAssignments;
      if (isSelected) {
        newServices = b.services.filter(s => s.id !== service.id);
        newAssignments = { ...b.staffAssignments };
        delete newAssignments[service.id];
      } else {
        newServices = [...b.services, service];
        newAssignments = { ...b.staffAssignments };
      }
      return { ...b, services: newServices, staffAssignments: newAssignments, date: null, time: null };
    });
  };

  const assignStaff = (serviceId, staffId) => {
    setBk(b => ({ ...b, staffAssignments: { ...b.staffAssignments, [serviceId]: staffId }, date: null, time: null }));
  };

  const toggleCategory = (cat) => { setOpenCats(prev => ({ ...prev, [cat]: !prev[cat] })); };

  const clinicServices = SERVICES.filter(s => s.sec === "clinic");
  const salonServices = SERVICES.filter(s => s.sec === "salon");
  const clinicCats = [...new Set(clinicServices.map(s => s.cat))];
  const salonCats = [...new Set(salonServices.map(s => s.cat))];

  const selectedServices = bk.services;
  const total = totalPrice(selectedServices);
  const duration = totalDur(selectedServices);
  // Check if every selected service has a staff assigned
  const allStaffAssigned = selectedServices.length > 0 && selectedServices.every(s => bk.staffAssignments[s.id]);

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}><SlikMark /><div>Loading...</div></div>;

  // ==================== STEP 1: SERVICE SELECTION ====================
  if (step === 1 && view === "book" && !done) {
    return (
      <div style={{ background: B.blush, minHeight: "100vh" }}>
        <style>{CSS}</style>
        <div style={{ background: B.plumDark, padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 100 }}>
          <SlikMark light small />
          <div style={{ display: "flex", gap: 8 }}>
            {[["book", "Book"], ["customer", "My Bookings"], ["admin", "Admin"]].map(([k, l]) => (
              <button key={k} onClick={() => { setView(k); if (k === "book") { setStep(1); setBk(initBk()); setDone(false); } }}
                style={{ padding: "6px 16px", borderRadius: "50px", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, background: view === k ? "rgba(196,150,58,0.25)" : "transparent", color: view === k ? B.goldLight : "rgba(255,255,255,0.7)" }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {view === "admin" && (!pwd || pwdErr ? <AdminLogin pwd={pwd} setPwd={setPwd} pwdErr={pwdErr} onLogin={() => pwd === ADMIN_PASSWORD ? setPwdErr(false) : setPwdErr(true)} /> : <AdminDash bks={bks} onUpdate={updateStatus} />)}
        {view === "customer" && <CustomerPanel bks={bks} onCancel={id => updateStatus(id, "cancelled")} />}

        {view === "book" && (
          <div style={{ maxWidth: 1400, margin: "0 auto", padding: "30px 20px" }}>
            <div style={{ textAlign: "center", marginBottom: 30 }}>
              <h1 className="pf" style={{ fontSize: "clamp(28px,5vw,48px)", fontWeight: 600, color: B.ink }}>Book Your <span style={{ color: B.plum }}>Treatment</span></h1>
              <GoldLine />
              <div className="nu" style={{ color: B.muted, marginTop: 10 }}>Select any treatments from Clinic or Salon</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* CLINIC COLUMN */}
              <div className="section-card section-clinic">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <span style={{ fontSize: 32 }}>💎</span>
                  <div><h2 className="pf" style={{ fontSize: 22, fontWeight: 600, color: B.plum }}>Aesthetic Clinic</h2><div className="nu" style={{ fontSize: 12, color: B.muted }}>Medical-grade aesthetic treatments</div></div>
                </div>
                {clinicCats.map(cat => {
                  const catServices = clinicServices.filter(s => s.cat === cat);
                  const isOpen = openCats[`clinic_${cat}`] !== false;
                  return (
                    <div key={`clinic_${cat}`} style={{ marginBottom: 12 }}>
                      <div className="cat-header" onClick={() => toggleCategory(`clinic_${cat}`)}><span className="pf" style={{ fontWeight: 600 }}>{cat}</span><span>{isOpen ? "▼" : "▶"}</span></div>
                      {isOpen && catServices.map(service => (
                        <div key={service.id} className={`service-item ${selectedServices.some(s => s.id === service.id) ? "service-sel" : ""}`} onClick={() => toggleService(service)}>
                          <div><div className="nu" style={{ fontWeight: 500 }}>{service.name}</div></div>
                          <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, color: B.plum }}>£{service.price}</div><div style={{ fontSize: 10, color: B.muted }}>{service.dur} min</div></div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* SALON COLUMN */}
              <div className="section-card section-salon">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <span style={{ fontSize: 32 }}>🌸</span>
                  <div><h2 className="pf" style={{ fontSize: 22, fontWeight: 600, color: B.gold }}>Beauty Salon</h2><div className="nu" style={{ fontSize: 12, color: B.muted }}>Hair, nails, waxing, massage & more</div></div>
                </div>
                {salonCats.map(cat => {
                  const catServices = salonServices.filter(s => s.cat === cat);
                  const isOpen = openCats[`salon_${cat}`] !== false;
                  return (
                    <div key={`salon_${cat}`} style={{ marginBottom: 12 }}>
                      <div className="cat-header" onClick={() => toggleCategory(`salon_${cat}`)}><span className="pf" style={{ fontWeight: 600 }}>{cat}</span><span>{isOpen ? "▼" : "▶"}</span></div>
                      {isOpen && catServices.map(service => (
                        <div key={service.id} className={`service-item ${selectedServices.some(s => s.id === service.id) ? "service-sel" : ""}`} onClick={() => toggleService(service)}>
                          <div><div className="nu" style={{ fontWeight: 500 }}>{service.name}</div></div>
                          <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, color: B.gold }}>£{service.price}</div><div style={{ fontSize: 10, color: B.muted }}>{service.dur} min</div></div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedServices.length > 0 && (
              <div style={{ position: "sticky", bottom: 20, marginTop: 30, background: B.white, borderRadius: 16, padding: "16px 24px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", border: `2px solid ${B.gold}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <div><div className="nu" style={{ fontSize: 12, fontWeight: 700, color: B.muted }}>SELECTED SERVICES</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>{selectedServices.map(s => (<div key={s.id} className="selected-pill" style={{ background: s.sec === "clinic" ? B.plumBg : B.champagne }}><span style={{ fontSize: 13 }}>{s.name} (£{s.price})</span><button onClick={() => toggleService(s)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: B.muted }}>✕</button></div>))}</div>
                  </div>
                  <div style={{ textAlign: "right" }}><div className="nu" style={{ fontSize: 24, fontWeight: 700, color: B.plum }}>£{total}</div><div className="nu" style={{ fontSize: 12, color: B.muted }}>{duration} min total</div>
                    <button className="btn-plum" style={{ marginTop: 8 }} onClick={() => setStep(2)}>Continue to Staff →</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {done && <SuccessScreen id={lastId} bk={bk} onNew={() => { setDone(false); setStep(1); setBk(initBk()); }} />}
      </div>
    );
  }

  // ==================== STEP 2: STAFF ASSIGNMENT (FIXED - No "one therapist does all" constraint) ====================
  if (step === 2 && view === "book" && !done) {
    return (
      <div style={{ background: B.blush, minHeight: "100vh" }}>
        <style>{CSS}</style>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 20px" }}>
          <button className="btn-out" onClick={() => setStep(1)} style={{ marginBottom: 20 }}>← Back to Services</button>
          
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <h2 className="pf" style={{ fontSize: 28, fontWeight: 600, color: B.ink }}>Choose Your Specialists</h2>
            <div className="nu" style={{ color: B.muted, fontSize: 13, marginTop: 8 }}>Select a therapist for each treatment</div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {selectedServices.map(service => {
              // Find staff who can perform THIS SPECIFIC service (based on category)
              const eligibleStaff = STAFF.filter(staff => staff.cats.includes(service.cat));
              const currentAssignment = bk.staffAssignments[service.id];
              
              return (
                <div key={service.id} style={{ background: B.white, borderRadius: 16, padding: 20, border: `1px solid ${B.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${B.border}` }}>
                    <div>
                      <span className={service.sec === "clinic" ? "badge-c" : "badge-s"} style={{ marginRight: 12 }}>{service.sec === "clinic" ? "Clinic" : "Salon"}</span>
                      <span className="pf" style={{ fontSize: 18, fontWeight: 600 }}>{service.name}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 700, color: service.sec === "clinic" ? B.plum : B.gold }}>£{service.price}</div>
                      <div style={{ fontSize: 11, color: B.muted }}>{service.dur} min</div>
                    </div>
                  </div>
                  
                  <div className="nu" style={{ fontSize: 12, color: B.muted, marginBottom: 12 }}>Select a specialist for this treatment:</div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
                    {eligibleStaff.map(staff => (
                      <div key={staff.id} 
                        className={`staff-option ${currentAssignment === staff.id ? "staff-selected" : ""}`}
                        onClick={() => assignStaff(service.id, staff.id)}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: staff.section === "clinic" ? B.plum : B.gold, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 16 }}>{staff.init}</div>
                        <div style={{ flex: 1 }}>
                          <div className="pf" style={{ fontWeight: 600 }}>{staff.name}</div>
                          <div className="nu" style={{ fontSize: 11, color: B.muted }}>{staff.role}</div>
                          <div className="nu" style={{ fontSize: 10, color: B.gold }}>★ {staff.rating} ({staff.reviews} reviews)</div>
                        </div>
                        {currentAssignment === staff.id && <div style={{ color: B.green, fontSize: 18 }}>✓</div>}
                      </div>
                    ))}
                  </div>
                  
                  {eligibleStaff.length === 0 && (
                    <div className="nu" style={{ background: "#FFF3CD", color: "#856404", padding: "10px 16px", borderRadius: 8, fontSize: 13 }}>
                      ⚠️ No staff available for this treatment yet. Please contact us directly.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <NavRow onBack={() => setStep(1)} onNext={() => setStep(3)} canNext={allStaffAssigned} />
        </div>
      </div>
    );
  }

  // ==================== STEP 3: DATE & TIME ====================
  if (step === 3 && view === "book" && !done) {
    const today = new Date();
    const days = calDays(cal.y, cal.m);
    const isSun = isSunday(bk.date);
    const slots = isSun ? SLOTS_SUNDAY : SLOTS_WEEKDAY;
    const dur = totalDur(selectedServices);
    const needed = Math.ceil(dur / 30);
    
    // Get all assigned staff names to check availability
    const assignedStaffIds = Object.values(bk.staffAssignments);
    const assignedStaffNames = assignedStaffIds.map(id => STAFF.find(s => s.id === id)?.name);
    
    // Check if ANY assigned staff is already booked at the proposed time
    const bookedStarts = bks
      .filter(b => assignedStaffNames.includes(b.staff_name) && b.date === bk.date && b.status !== "cancelled")
      .map(b => b.time);
    
    const slotOk = (idx) => {
      if (!bk.date) return false;
      for (let i = 0; i < needed; i++) {
        if (idx + i >= slots.length) return false;
        if (bookedStarts.includes(slots[idx + i])) return false;
      }
      return true;
    };
    
    const isPastDate = (day) => {
      if (!day) return true;
      const d = new Date(cal.y, cal.m, day);
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return d < todayDate;
    };
    
    const shiftMonth = (dir) => setCal(c => { let m = c.m + dir, y = c.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } return { y, m }; });

    return (
      <div style={{ background: B.blush, minHeight: "100vh" }}>
        <style>{CSS}</style>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 20px" }}>
          <button className="btn-out" onClick={() => setStep(2)} style={{ marginBottom: 20 }}>← Back to Staff</button>
          
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h2 className="pf" style={{ fontSize: 28, fontWeight: 600, color: B.ink }}>Choose Date & Time</h2>
            <div className="nu" style={{ color: B.muted, marginTop: 8 }}>Mon–Sat 9am–6pm · Sun 11am–5pm</div>
            {needed > 1 && <div className="nu" style={{ fontSize: 12, color: B.plum, marginTop: 10, background: B.plumBg, display: "inline-block", padding: "5px 15px", borderRadius: 20 }}>⏱️ {needed} time slot{needed > 1 ? "s" : ""} needed ({dur} min total)</div>}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div style={{ background: B.white, borderRadius: 16, padding: 20, border: `1px solid ${B.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <button className="btn-out" onClick={() => shiftMonth(-1)} style={{ padding: "6px 14px", fontSize: 12 }}>‹</button>
                <span className="nu" style={{ fontWeight: 700, fontSize: 15 }}>{MONTHS[cal.m]} {cal.y}</span>
                <button className="btn-out" onClick={() => shiftMonth(1)} style={{ padding: "6px 14px", fontSize: 12 }}>›</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, textAlign: "center" }}>
                {DSHORT.map(d => <div key={d} className="nu" style={{ fontSize: 11, color: B.muted, padding: "6px 0", fontWeight: 700 }}>{d}</div>)}
                {days.map((day, idx) => {
                  const dStr = day ? toDS(cal.y, cal.m, day) : null;
                  const isPast = isPastDate(day);
                  const isSelected = bk.date === dStr;
                  return (<div key={idx} className={`cal-day ${isPast ? "cal-day-dis" : ""} ${isSelected && !isPast ? "cal-day-sel" : ""}`} onClick={() => !isPast && day && setBk(b => ({ ...b, date: dStr, time: null }))} style={{ padding: "8px 4px" }}>{day || ""}</div>);
                })}
              </div>
            </div>

            <div style={{ background: B.white, borderRadius: 16, padding: 20, border: `1px solid ${B.border}` }}>
              {bk.date ? (
                <>
                  <div className="nu" style={{ fontSize: 13, fontWeight: 600, color: B.plum, marginBottom: 12 }}>📅 {fmtLong(bk.date)}{isSunday(bk.date) && <span style={{ background: B.champagne, color: B.gold, fontSize: 10, marginLeft: 10, padding: "2px 10px", borderRadius: 20 }}>Sunday</span>}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                    {slots.map((t, idx) => {
                      const ok = slotOk(idx);
                      const isSelected = bk.time === t;
                      return (<div key={t} className={`tslot ${!ok ? "tslot-dis" : ""} ${isSelected && ok ? "tslot-sel" : ""}`} onClick={() => ok && setBk(b => ({ ...b, time: t }))}>{t}</div>);
                    })}
                  </div>
                  {bk.time && (<div className="nu" style={{ fontSize: 12, marginTop: 16, background: B.plumBg, color: B.plum, padding: "10px 14px", borderRadius: 10, textAlign: "center", fontWeight: 600 }}>✓ {bk.time} → {slots[slots.indexOf(bk.time) + needed - 1]} ({dur} min)</div>)}
                </>
              ) : (<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 200, color: B.muted, textAlign: "center" }}><div><div style={{ fontSize: 40, marginBottom: 10 }}>📅</div><div className="nu">Select a date to see available times</div></div></div>)}
            </div>
          </div>
          <NavRow onBack={() => setStep(2)} onNext={() => setStep(4)} canNext={!!(bk.date && bk.time)} />
        </div>
      </div>
    );
  }

  // ==================== STEP 4: CLIENT DETAILS ====================
  if (step === 4 && view === "book" && !done) {
    const upd = (k, v) => setBk(b => ({ ...b, client: { ...b.client, [k]: v } }));
    const { client: cl } = bk;
    const valid = cl.name.trim() && cl.email.includes("@") && cl.phone.trim().length >= 7;
    const dur = totalDur(selectedServices);
    const inpStyle = { padding: "11px 14px", border: `1.5px solid ${B.border}`, borderRadius: 10, fontSize: 13, width: "100%", fontFamily: "'Nunito', sans-serif" };

    return (
      <div style={{ background: B.blush, minHeight: "100vh" }}>
        <style>{CSS}</style>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "40px 20px" }}>
          <button className="btn-out" onClick={() => setStep(3)} style={{ marginBottom: 20 }}>← Back to Time</button>
          <div style={{ textAlign: "center", marginBottom: 24 }}><h2 className="pf" style={{ fontSize: 28, fontWeight: 600, color: B.ink }}>Your Details</h2><div className="nu" style={{ color: B.muted, marginTop: 6 }}>Confirmation email sent instantly · No deposit required</div></div>
          <div style={{ background: B.white, borderRadius: 16, padding: 28, border: `1px solid ${B.border}` }}>
            <div style={{ background: B.plumBg, borderRadius: 12, padding: "14px 18px", marginBottom: 24 }}>
              <div className="nu" style={{ fontSize: 12, fontWeight: 700, color: B.plum, marginBottom: 10 }}>📋 Your Selection</div>
              {selectedServices.map((s, i) => {
                const assignedStaff = STAFF.find(st => st.id === bk.staffAssignments[s.id]);
                return (<div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: i < selectedServices.length - 1 ? `1px solid ${B.border}` : "none" }}><span className="nu" style={{ fontSize: 13 }}>{s.name} <span className="nu" style={{ fontSize: 10, color: B.gold }}>with {assignedStaff?.name}</span></span><span className="nu" style={{ fontWeight: 600 }}>£{s.price}</span></div>);
              })}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: `2px solid ${B.border}` }}><span className="nu" style={{ fontWeight: 700 }}>Total</span><span className="nu" style={{ fontSize: 18, fontWeight: 700, color: B.plum }}>£{total} · {dur} min</span></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}><div><SLbl c="Full Name *"/><input style={inpStyle} value={cl.name} onChange={e => upd("name", e.target.value)} placeholder="Your full name"/></div><div><SLbl c="Phone *"/><input style={inpStyle} value={cl.phone} onChange={e => upd("phone", e.target.value)} placeholder="+44..."/></div></div>
            <div style={{ marginBottom: 14 }}><SLbl c="Email Address *"/><input style={inpStyle} type="email" value={cl.email} onChange={e => upd("email", e.target.value)} placeholder="your@email.com"/></div>
            <div><SLbl c="Notes (optional)"/><textarea style={{ ...inpStyle, minHeight: 80, resize: "vertical" }} value={cl.notes} onChange={e => upd("notes", e.target.value)} placeholder="Allergies, preferences, special requests..."/></div>
          </div>
          <NavRow onBack={() => setStep(3)} onNext={() => setStep(5)} canNext={valid} label="Review Booking →" />
        </div>
      </div>
    );
  }

  // ==================== STEP 5: CONFIRM ====================
  if (step === 5 && view === "book" && !done) {
    const staffAssignmentsList = Object.entries(bk.staffAssignments).map(([serviceId, staffId]) => ({ service: selectedServices.find(s => s.id === serviceId), staff: STAFF.find(s => s.id === staffId) }));
    const hasClinic = selectedServices.some(s => s.sec === "clinic");
    const hasSalon = selectedServices.some(s => s.sec === "salon");
    const accent = hasSalon && !hasClinic ? B.gold : B.plum;
    const btnClass = hasSalon && !hasClinic ? "btn-gold" : "btn-plum";

    return (
      <div style={{ background: B.blush, minHeight: "100vh" }}>
        <style>{CSS}</style>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "40px 20px" }}>
          <button className="btn-out" onClick={() => setStep(4)} style={{ marginBottom: 20 }}>← Back to Details</button>
          <div style={{ textAlign: "center", marginBottom: 24 }}><h2 className="pf" style={{ fontSize: 28, fontWeight: 600, color: B.ink }}>Confirm Your Booking</h2></div>
          <div style={{ background: B.white, borderRadius: 16, padding: 28, border: `1px solid ${B.border}`, marginBottom: 24 }}>
            <div className="nu" style={{ fontSize: 12, fontWeight: 700, color: B.muted, marginBottom: 10 }}>TREATMENTS & SPECIALISTS</div>
            {staffAssignmentsList.map((item, i) => (<div key={item.service.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < staffAssignmentsList.length - 1 ? `1px solid ${B.border}` : "none" }}><span>{item.service.name} <span className="nu" style={{ fontSize: 11, color: B.gold }}>with {item.staff?.name}</span></span><span style={{ fontWeight: 600 }}>£{item.service.price}</span></div>))}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, paddingTop: 10, borderTop: `2px solid ${B.border}` }}><span className="pf" style={{ fontSize: 20, fontWeight: 600 }}>Total</span><span style={{ fontSize: 24, fontWeight: 700, color: accent }}>£{total}</span></div>
            <div className="nu" style={{ fontSize: 12, color: B.muted, textAlign: "right", marginTop: 4 }}>{totalDur(selectedServices)} minutes · Pay on the day</div>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${B.border}` }}><div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span className="nu" style={{ color: B.muted }}>Date</span><span style={{ fontWeight: 500 }}>{fmtLong(bk.date)}</span></div><div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span className="nu" style={{ color: B.muted }}>Time</span><span style={{ fontWeight: 500 }}>{bk.time}</span></div><div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span className="nu" style={{ color: B.muted }}>Name</span><span style={{ fontWeight: 500 }}>{bk.client.name}</span></div><div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span className="nu" style={{ color: B.muted }}>Email</span><span style={{ fontWeight: 500 }}>{bk.client.email}</span></div><div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span className="nu" style={{ color: B.muted }}>Phone</span><span style={{ fontWeight: 500 }}>{bk.client.phone}</span></div></div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}><button className="btn-out" onClick={() => setStep(4)}>← Back</button><button className={btnClass} onClick={confirmBooking} disabled={sending}>{sending ? "Sending confirmation..." : "Confirm & Send Email ✓"}</button></div>
        </div>
      </div>
    );
  }
  return null;
}

// ─── ADMIN DASHBOARD ─────────────────────────────────────────
function AdminDash({ bks, onUpdate }) {
  const [filter, setFilter] = useState("all");
  const visible = bks.filter(b => filter === "all" || b.status === filter).sort((a, c) => (c.date || "").localeCompare(a.date || ""));
  const stats = { total: bks.length, confirmed: bks.filter(b => b.status === "confirmed").length, completed: bks.filter(b => b.status === "completed").length, cancelled: bks.filter(b => b.status === "cancelled").length, revenue: bks.filter(b => b.status === "completed").reduce((s, b) => s + (b.service_price || 0), 0) };
  return (<div style={{ maxWidth: 1000, margin: "0 auto", padding: "30px 20px" }}><h2 className="pf" style={{ fontSize: 28, marginBottom: 20 }}>Admin Dashboard</h2><div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>{Object.entries(stats).map(([k, v]) => (<div key={k} style={{ background: B.white, padding: "12px 20px", borderRadius: 12, border: `1px solid ${B.border}` }}><strong>{k}:</strong> {v}</div>))}</div><div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>{["all", "confirmed", "completed", "cancelled"].map(f => (<button key={f} className="btn-out" onClick={() => setFilter(f)} style={{ padding: "6px 16px", fontSize: 12, background: filter === f ? B.plum : "transparent", color: filter === f ? "white" : B.muted, borderColor: filter === f ? B.plum : B.border }}>{f.toUpperCase()}</button>))}</div>{visible.map(b => (<div key={b.id} style={{ background: B.white, borderRadius: 12, padding: 16, marginBottom: 10, border: `1px solid ${B.border}` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}><div><strong>{b.client_name}</strong><br /><span style={{ fontSize: 12, color: B.muted }}>{b.service_name}</span></div><div style={{ fontSize: 13 }}>{b.date} at {b.time}</div><div style={{ fontWeight: 700 }}>£{b.service_price}</div><div><span className="badge-c">{b.status}</span></div><select value={b.status} onChange={e => onUpdate(b.id, e.target.value)} style={{ padding: 8, borderRadius: 8, border: `1px solid ${B.border}` }}><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select></div></div>))}</div>);
}

// ─── CUSTOMER PANEL ──────────────────────────────────────────
function CustomerPanel({ bks, onCancel }) {
  const [email, setEmail] = useState("");
  const [results, setResults] = useState(null);
  const lookup = () => { const found = bks.filter(b => b.client_email?.toLowerCase() === email.toLowerCase()); setResults(found.length ? found.sort((a, c) => (c.date || "").localeCompare(a.date || "")) : null); };
  return (<div style={{ maxWidth: 600, margin: "0 auto", padding: "40px 20px" }}><h2 className="pf" style={{ fontSize: 28, marginBottom: 20, textAlign: "center" }}>My Bookings</h2><div style={{ display: "flex", gap: 12, marginBottom: 24 }}><input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} style={{ flex: 1, padding: 12, borderRadius: 10, border: `1.5px solid ${B.border}`, fontSize: 14 }}/><button className="btn-plum" onClick={lookup} style={{ padding: "12px 24px" }}>Find</button></div>{results && results.map(b => (<div key={b.id} style={{ background: B.white, borderRadius: 12, padding: 18, marginBottom: 12, border: `1px solid ${B.border}` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}><div className="pf" style={{ fontSize: 16, fontWeight: 600 }}>{b.service_name}</div><span className="badge-c">{b.status}</span></div><div className="nu" style={{ fontSize: 13, color: B.muted }}>{fmtLong(b.date)} at {b.time}</div><div className="nu" style={{ fontWeight: 700, marginTop: 8 }}>£{b.service_price} — Pay on the day</div>{b.status === "confirmed" && (<button className="btn-out" onClick={() => onCancel(b.id)} style={{ marginTop: 12, padding: "6px 16px", fontSize: 12 }}>Cancel Appointment</button>)}</div>))}</div>);
}

// ─── SUCCESS SCREEN ──────────────────────────────────────────
function SuccessScreen({ id, bk, onNew }) {
  return (<div style={{ textAlign: "center", padding: "60px 20px" }}><div style={{ fontSize: 48, marginBottom: 16, color: B.gold }}>✨</div><h2 className="pf" style={{ fontSize: 36, fontWeight: 600, marginBottom: 8 }}>Booking Confirmed!</h2><p className="nu" style={{ color: B.muted }}>Confirmation email sent to <strong>{bk.client.email}</strong></p><div style={{ background: B.white, borderRadius: 16, padding: 24, maxWidth: 400, margin: "30px auto", border: `1px solid ${B.border}` }}><div className="nu" style={{ fontSize: 11, fontWeight: 700, color: B.muted, letterSpacing: "1px" }}>REFERENCE</div><div className="nu" style={{ fontSize: 20, fontWeight: 700, color: B.plum, marginBottom: 16 }}>{id}</div><div className="nu" style={{ fontSize: 13, lineHeight: 1.8 }}><div>{fmtLong(bk.date)} at {bk.time}</div><div style={{ fontWeight: 700, marginTop: 8 }}>£{totalPrice(bk.services)} — Pay on the day</div></div></div><button className="btn-plum" onClick={onNew}>Book Another Appointment</button></div>);
}


// Admin login component
function AdminLogin({ pwd, setPwd, pwdErr, onLogin }) {
  return (<div style={{ maxWidth: 400, margin: "80px auto", padding: 30, background: B.white, borderRadius: 16, textAlign: "center" }}><SlikMark /><h3 style={{ marginTop: 20 }}>Admin Login</h3><input type="password" placeholder="Password" value={pwd} onChange={e => setPwd(e.target.value)} onKeyDown={e => e.key === "Enter" && onLogin()} style={{ width: "100%", padding: 12, margin: "20px 0", border: `1px solid ${pwdErr ? B.red : B.border}`, borderRadius: 8 }} />{pwdErr && <p style={{ color: B.red }}>Incorrect password</p>}<button className="btn-plum" onClick={onLogin}>Sign In</button></div>);
}
